import React, { useState } from "react";
import { toast } from "sonner";
import { Link2, Copy, Search, UserPlus, Check, Mail, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function MeetingInvitePanel({ meetingId, joinCode }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [invitedIds, setInvitedIds] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailedList, setEmailedList] = useState([]);

  const joinLink = `${window.location.origin}/meeting/join/${joinCode}`;

  const copyLink = () => {
    navigator.clipboard.writeText(joinLink).then(() => toast.success("Invite link copied!")).catch(() => toast.error(`Copy manually: ${joinLink}`));
  };

  const searchPeople = async (e) => {
    e.preventDefault();
    if (!query.trim() || query.trim().length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/friends/search?q=${encodeURIComponent(query.trim())}`);
      setResults(data);
    } catch {
      toast.error("Could not search people");
    } finally {
      setSearching(false);
    }
  };

  const invitePerson = async (userId) => {
    try {
      await api.post(`/meetings/${meetingId}/invite`, { user_ids: [userId] });
      setInvitedIds((prev) => [...prev, userId]);
      toast.success("Invite sent!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send invite");
    }
  };

  const inviteByEmail = async (e) => {
    e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { toast.error("Enter a valid email address"); return; }
    setSendingEmail(true);
    try {
      await api.post(`/meetings/${meetingId}/invite`, { emails: [email] });
      setEmailedList((prev) => [...prev, email]);
      setEmailInput("");
      toast.success(`Invite emailed to ${email}!`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send email invite");
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-6 space-y-5" data-testid="meeting-invite-panel">
      <div>
        <h3 className="font-heading font-semibold text-white mb-1 flex items-center gap-2"><Link2 className="w-4 h-4 text-cyan-400" /> Shareable Link</h3>
        <p className="text-sm text-slate-400 mb-3">Send this on WhatsApp/Email — anyone who opens it joins instantly, no code typing needed.</p>
        <div className="flex items-center gap-2">
          <input readOnly value={joinLink} data-testid="meeting-invite-link-input" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-300 focus:outline-none" />
          <button onClick={copyLink} data-testid="meeting-copy-link-btn" className="flex items-center gap-1.5 text-xs font-semibold bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 px-3 py-2 rounded-lg shrink-0">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-heading font-semibold text-white mb-1 flex items-center gap-2"><Mail className="w-4 h-4 text-cyan-400" /> Invite by Email</h3>
        <p className="text-sm text-slate-400 mb-3">Enter any email — we'll send them the join link directly, no Cloudivo account needed.</p>
        <form onSubmit={inviteByEmail} noValidate className="flex items-center gap-2 mb-2">
          <input
            type="text" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="name@example.com" data-testid="meeting-invite-email-input"
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-400"
          />
          <button type="submit" disabled={sendingEmail} data-testid="meeting-invite-email-send-btn" className="flex items-center gap-1.5 text-xs font-semibold bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 px-3 py-2 rounded-lg disabled:opacity-60 shrink-0">
            <Send className="w-3.5 h-3.5" /> {sendingEmail ? "Sending..." : "Send"}
          </button>
        </form>
        {emailedList.length > 0 && (
          <div className="space-y-1" data-testid="meeting-invite-emailed-list">
            {emailedList.map((email) => (
              <p key={email} className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> {email}</p>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-heading font-semibold text-white mb-1 flex items-center gap-2"><UserPlus className="w-4 h-4 text-cyan-400" /> Add People</h3>
        <p className="text-sm text-slate-400 mb-3">Search for a Cloudivo learner by name and invite them directly — they'll get a notification with the link.</p>
        <form onSubmit={searchPeople} className="flex items-center gap-2 mb-3">
          <input
            value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name..." data-testid="meeting-invite-search-input"
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-400"
          />
          <button type="submit" disabled={searching} data-testid="meeting-invite-search-btn" className="text-sm font-semibold bg-white/10 hover:bg-white/15 text-white px-3 py-2 rounded-lg disabled:opacity-60">
            <Search className="w-4 h-4" />
          </button>
        </form>
        <div className="space-y-2" data-testid="meeting-invite-search-results">
          {results.length === 0 && query.trim().length >= 2 && !searching && (
            <p className="text-xs text-slate-500 text-center py-2">No matching learners found.</p>
          )}
          {results.map((r) => {
            const invited = invitedIds.includes(r.user_id);
            return (
              <div key={r.user_id} className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2" data-testid={`meeting-invite-result-${r.user_id}`}>
                <div className="w-7 h-7 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-300 text-xs font-bold shrink-0">{r.name?.[0]?.toUpperCase() || "?"}</div>
                <span className="flex-1 text-sm text-white truncate">{r.name}</span>
                <button
                  onClick={() => invitePerson(r.user_id)} disabled={invited} data-testid={`meeting-invite-send-btn-${r.user_id}`}
                  className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${invited ? "bg-emerald-400/15 text-emerald-400" : "bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30"}`}
                >
                  {invited ? <><Check className="w-3 h-3" /> Invited</> : "Invite"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
